"use client"

import type React from "react"

import { useState, useEffect } from "react"
import {
  LuX,
  LuMapPin,
  LuPlus,
  LuLoader,
  LuNetwork,
  LuInfo,
  LuUser,
  LuGlobe,
  LuCheck,
  LuChevronRight,
  LuFileText,
  LuClipboard,
  LuBuilding,
  LuCalendar,
  LuPhone,
} from "react-icons/lu"
import { CloseModal } from "./closemodal"
import { SelectWithSearch } from "../../utils/select-with-search"
import axiosInstance from "../../libs/axios"

interface Client {
  id: number
  client_name: string
  status: string
}

interface Activity {
  id: number
  activity_name: string
  status: string
}

interface Project {
  id: number
  project_name: string
  status: string
  client?: Client
  activity?: Activity
}

interface AddSiteData {
  site_id: string
  project_id: number
  activity?: string
  reference_number?: string
  client_allocation_date?: string
  site_name?: string
  site_address?: string
  opco_name?: string
  opco_id?: string
  state_circle_id?: number
  district_id?: number
  pincode?: string
  site_latitude?: number
  site_longitude?: number
  site_priority?: string
  pr_number?: string
  pr_date?: string
  po_number?: string
  po_date?: string
  is_po_no_shared_at_allocation?: boolean
  technician_name?: string
  technician_number?: string
  fse_name?: string
  fse_number?: string
  aom_name?: string
  aom_number?: string
  client_project_name?: string
  customer_requirement?: string
  site_information1?: string
  site_information2?: string
  site_information3?: string
  contact_person?: string
  contact_number?: string
  site_engineer_userid?: number
  ta_userid?: number
  mis_userid?: number
  report_designer_userid?: number
  validator_userid?: number
}

interface AddSiteOffcanvasProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: AddSiteData) => Promise<void>
  projects: Project[]
  loading?: boolean
}

export const AddSiteOffcanvas: React.FC<AddSiteOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  projects,
  loading = false,
}) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [formData, setFormData] = useState<AddSiteData>({
    site_id: "",
    project_id: 0,
    activity: "",
    reference_number: "",
    client_allocation_date: "",
    site_name: "",
    site_address: "",
    opco_name: "",
    opco_id: "",
    state_circle_id: undefined,
    district_id: undefined,
    pincode: "",
    site_latitude: undefined,
    site_longitude: undefined,
    site_priority: "medium",
    pr_number: "",
    pr_date: "",
    po_number: "",
    po_date: "",
    is_po_no_shared_at_allocation: false,
    technician_name: "",
    technician_number: "",
    fse_name: "",
    fse_number: "",
    aom_name: "",
    aom_number: "",
    client_project_name: "",
    customer_requirement: "",
    site_information1: "",
    site_information2: "",
    site_information3: "",
    contact_person: "",
    contact_number: "",
    site_engineer_userid: undefined,
    ta_userid: undefined,
    mis_userid: undefined,
    report_designer_userid: undefined,
    validator_userid: undefined,
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [submitting, setSubmitting] = useState(false)
  const [stepValidation, setStepValidation] = useState({
    1: false,
    2: false,
    3: false,
    4: false,
  })
  const [selectedState, setSelectedState] = useState<{ id: number; label: string; value: string | number } | null>(null)
  const [selectedDistrict, setSelectedDistrict] = useState<{ id: number; label: string; value: string | number } | null>(null)
  const [visibleSiteInfoFields, setVisibleSiteInfoFields] = useState(1)
  const [users, setUsers] = useState<any[]>([])
  const [usersLoading, setUsersLoading] = useState(false)

  // Fetch users
  const fetchUsers = async (filters: any = {}, sort: any = {}, page: number = 1) => {
    setUsersLoading(true);
    try {
      // Build query parameters object
      const queryParams: any = {
        page: page,
        limit: 100, // Fetch more for select
        ...filters,
        ...sort
      };

      // Remove undefined/null values and convert to strings
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === undefined || queryParams[key] === null) {
          delete queryParams[key];
        } else {
          queryParams[key] = queryParams[key].toString();
        }
      });

      const response = await axiosInstance.get('/users/list', {
        params: queryParams
      });

      if (response.data.success) {
        setUsers(response.data.data.users || []);
      } else {
        console.error('API returned success: false');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const steps = [
    {
      number: 1,
      title: "Site Info",
      description: "Identification, location, and additional details",
      icon: LuInfo,
    },
    {
      number: 2,
      title: "Team & Contact",
      description: "Team members and contact",
      icon: LuUser,
    },
    {
      number: 3,
      title: "Purchase Order",
      description: "PO and PR details",
      icon: LuFileText,
    },
    {
      number: 4,
      title: "User Assignments",
      description: "Assign users to roles",
      icon: LuUser,
    },
  ]

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        site_id: "",
        project_id: 0,
        activity: "",
        reference_number: "",
        client_allocation_date: "",
        site_name: "",
        site_address: "",
        opco_name: "",
        opco_id: "",
        state_circle_id: undefined,
        district_id: undefined,
        pincode: "",
        site_latitude: undefined,
        site_longitude: undefined,
        site_priority: "medium",
        pr_number: "",
        pr_date: "",
        po_number: "",
        po_date: "",
        is_po_no_shared_at_allocation: false,
        technician_name: "",
        technician_number: "",
        fse_name: "",
        fse_number: "",
        aom_name: "",
        aom_number: "",
        client_project_name: "",
        customer_requirement: "",
        site_information1: "",
        site_information2: "",
        site_information3: "",
        contact_person: "",
        contact_number: "",
        site_engineer_userid: undefined,
        ta_userid: undefined,
        mis_userid: undefined,
        report_designer_userid: undefined,
        validator_userid: undefined,
      })
      setErrors({})
      setSubmitting(false)
      setCurrentStep(1)
      setStepValidation({ 1: false, 2: false, 3: false, 4: false })
      setSelectedState(null)
      setSelectedDistrict(null)
      setVisibleSiteInfoFields(1)
      fetchUsers()
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !loading && !submitting) {
        setShowCloseModal(true);
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen, loading, submitting])

  // Update form data
  const updateFormData = (field: keyof AddSiteData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }

    // Revalidate current step
    setTimeout(validateCurrentStep, 0)
  }

  // Validate specific step
  const validateStep = (step: number) => {
    const newErrors: { [key: string]: string } = {}
    let isValid = true

    switch (step) {
      case 1:
        // Site Info validation (basic + location + additional)
        if (!formData.site_id.trim()) {
          newErrors.site_id = "Site ID is required"
          isValid = false
        } else if (formData.site_id.trim().length < 3) {
          newErrors.site_id = "Site ID must be at least 3 characters"
          isValid = false
        } else if (!/^[A-Z0-9_-]+$/i.test(formData.site_id)) {
          newErrors.site_id = "Site ID must contain only alphanumeric characters, hyphens, and underscores"
          isValid = false
        }

        if (!formData.project_id) {
          newErrors.project_id = "Project selection is required"
          isValid = false
        }

        if (formData.site_latitude !== undefined && formData.site_latitude !== null) {
          const lat = Number(formData.site_latitude)
          if (isNaN(lat) || lat < 6 || lat > 38) {
            newErrors.site_latitude = "Latitude must be within India range (6 to 38)"
            isValid = false
          }
        }

        if (formData.site_longitude !== undefined && formData.site_longitude !== null) {
          const lng = Number(formData.site_longitude)
          if (isNaN(lng) || lng < 68 || lng > 98) {
            newErrors.site_longitude = "Longitude must be within India range (68 to 98)"
            isValid = false
          }
        }

        // if (formData.pincode && formData.pincode.trim()) {
        //   if (!/^\d{6}$/.test(formData.pincode.trim())) {
        //     newErrors.pincode = "Pincode must be exactly 6 digits"
        //     isValid = false
        //   }
        // }
        break

      case 2:
        // Team & Contact validation
        if (formData.contact_number && formData.contact_number.trim()) {
          const phoneRegex = /^[+]?[\d\s\-()]+$/
          if (!phoneRegex.test(formData.contact_number) || formData.contact_number.length > 15) {
            newErrors.contact_number = "Invalid contact number format"
            isValid = false
          }
        }

        if (formData.technician_number && formData.technician_number.trim()) {
          const phoneRegex = /^[+]?[\d\s\-()]+$/
          if (!phoneRegex.test(formData.technician_number) || formData.technician_number.length > 15) {
            newErrors.technician_number = "Invalid technician number format"
            isValid = false
          }
        }

        if (formData.fse_number && formData.fse_number.trim()) {
          const phoneRegex = /^[+]?[\d\s\-()]+$/
          if (!phoneRegex.test(formData.fse_number) || formData.fse_number.length > 15) {
            newErrors.fse_number = "Invalid FSE number format"
            isValid = false
          }
        }

        if (formData.aom_number && formData.aom_number.trim()) {
          const phoneRegex = /^[+]?[\d\s\-()]+$/
          if (!phoneRegex.test(formData.aom_number) || formData.aom_number.length > 15) {
            newErrors.aom_number = "Invalid AOM number format"
            isValid = false
          }
        }
        break

      case 3:
        // Purchase order validation
        if (formData.client_allocation_date && formData.client_allocation_date.trim()) {
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/
          if (!dateRegex.test(formData.client_allocation_date)) {
            newErrors.client_allocation_date = "Please use YYYY-MM-DD format"
            isValid = false
          }
        }

        if (formData.pr_date && formData.pr_date.trim()) {
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/
          if (!dateRegex.test(formData.pr_date)) {
            newErrors.pr_date = "Please use YYYY-MM-DD format"
            isValid = false
          }
        }

        if (formData.po_date && formData.po_date.trim()) {
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/
          if (!dateRegex.test(formData.po_date)) {
            newErrors.po_date = "Please use YYYY-MM-DD format"
            isValid = false
          }
        }
        break

      case 4:
        // User Assignments validation (no validation required)
        break
    }

    setErrors((prev) => ({ ...prev, ...newErrors }))
    setStepValidation((prev) => ({ ...prev, [step]: isValid }))
    return isValid
  }

  // Validate current step
  const validateCurrentStep = () => {
    return validateStep(currentStep)
  }

  // Handle step navigation
  const goToStep = (step: number) => {
    if (step > currentStep) {
      if (!validateCurrentStep()) {
        return
      }
    }

    if (step >= 1 && step <= 3) {
      setCurrentStep(step)
    }
  }

  // Handle next button
  const handleNext = () => {
    if (validateCurrentStep() && currentStep < 4) {
      setCurrentStep(currentStep + 1)
    }
  }

  // Handle previous button
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  // Handle form submission
  const handleSubmit = async () => {
    // Validate all steps
    const step1Valid = validateStep(1)
    const step2Valid = validateStep(2)
    const step3Valid = validateStep(3)
    const step4Valid = validateStep(4)

    if (!step1Valid || !step2Valid || !step3Valid || !step4Valid) {
      return
    }

    setSubmitting(true)
    try {
      const submitData = {
        ...formData,
        site_id: formData.site_id.trim(),
        activity: formData.activity?.trim() || undefined,
        reference_number: formData.reference_number?.trim() || undefined,
        site_name: formData.site_name?.trim() || undefined,
        site_address: formData.site_address?.trim() || undefined,
        opco_name: formData.opco_name?.trim() || undefined,
        opco_id: formData.opco_id?.trim() || undefined,
        state_circle_id: formData.state_circle_id || undefined,
        district_id: formData.district_id || undefined,
        pincode: formData.pincode?.trim() || undefined,
        pr_number: formData.pr_number?.trim() || undefined,
        po_number: formData.po_number?.trim() || undefined,
        technician_name: formData.technician_name?.trim() || undefined,
        technician_number: formData.technician_number?.trim() || undefined,
        fse_name: formData.fse_name?.trim() || undefined,
        fse_number: formData.fse_number?.trim() || undefined,
        aom_name: formData.aom_name?.trim() || undefined,
        aom_number: formData.aom_number?.trim() || undefined,
        client_project_name: formData.client_project_name?.trim() || undefined,
        customer_requirement: formData.customer_requirement?.trim() || undefined,
        site_information1: formData.site_information1?.trim() || undefined,
        site_information2: formData.site_information2?.trim() || undefined,
        site_information3: formData.site_information3?.trim() || undefined,
        contact_person: formData.contact_person?.trim() || undefined,
        contact_number: formData.contact_number?.trim() || undefined,
        site_latitude: formData.site_latitude || undefined,
        site_longitude: formData.site_longitude || undefined,
        site_engineer_userid: formData.site_engineer_userid || undefined,
        ta_userid: formData.ta_userid || undefined,
        mis_userid: formData.mis_userid || undefined,
        report_designer_userid: formData.report_designer_userid || undefined,
        validator_userid: formData.validator_userid || undefined,
      }

      await onSubmit(submitData)
      onClose()
    } catch (error) {
      console.error("Failed to create site:", error)
    } finally {
      setSubmitting(false)
    }
  }

  // Check if step is accessible
  const isStepAccessible = (step: number) => {
    if (step === 1) return true
    if (step === 2) return stepValidation[1]
    if (step === 3) return stepValidation[1] && stepValidation[2]
    if (step === 4) return stepValidation[1] && stepValidation[2] && stepValidation[3]
    return false
  }

  // Handle state selection
  const handleStateChange = (option: { id: number; label: string; value: string | number } | null) => {
    setSelectedState(option)
    setSelectedDistrict(null) // Clear district when state changes
    updateFormData("state_circle_id", option ? option.id : undefined)
    updateFormData("district_id", undefined)
  }

  // Handle district selection
  const handleDistrictChange = (option: { id: number; label: string; value: string | number } | null) => {
    setSelectedDistrict(option)
    updateFormData("district_id", option ? option.id : undefined)
  }

  // Filter active projects
  const activeProjects = projects.filter((project) => project.status === "ACTIVE")
  const selectedProject = activeProjects.find((p) => p.id === formData.project_id)

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={!loading && !submitting ? () => setShowCloseModal(true) : undefined}
      />

      {/* Offcanvas */}
      <div className="fixed top-0 right-0 h-full w-full max-w-7xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
        {/* Header - Responsive */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 truncate">Add New Site</h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
              Create a new site with complete project details
            </p>
          </div>
          <button
            onClick={() => setShowCloseModal(true)}
            className="ml-4 p-2 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
            disabled={loading || submitting}
          >
            <LuX className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Stepper - Responsive */}
        <div className="px-2 sm:px-6 py-3 sm:py-4 bg-white border-b border-gray-200 overflow-x-auto flex-shrink-0">
          <div className="flex items-center justify-between min-w-max">
            {steps.map((step, index) => {
              const Icon = step.icon
              const isActive = currentStep === step.number
              const isCompleted = stepValidation[step.number as 1 | 2 | 3 | 4]
              const isAccessible = isStepAccessible(step.number)

              return (
                <div key={step.number} className="flex items-center flex-1 min-w-0">
                  {/* Step Circle */}
                  <div className="flex items-center">
                    <button
                      onClick={() => goToStep(step.number)}
                      disabled={!isAccessible}
                      className={`relative flex items-center justify-center w-8 sm:w-10 h-8 sm:h-10 rounded-full border-2 transition-all duration-200 ${isActive
                        ? "border-blue-600 bg-blue-600 text-white"
                        : isCompleted
                          ? "border-green-600 bg-green-600 text-white"
                          : isAccessible
                            ? "border-gray-300 bg-white text-gray-500 hover:border-gray-400"
                            : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                    >
                      {isCompleted && !isActive ? (
                        <LuCheck className="w-3 sm:w-5 h-3 sm:h-5" />
                      ) : (
                        <Icon className="w-3 sm:w-4 h-3 sm:h-4" />
                      )}
                    </button>

                    {/* Step Info - Hidden on small screens */}
                    <div className="ml-2 sm:ml-3 min-w-0 hidden sm:block">
                      <p
                        className={`text-sm font-medium truncate ${isActive
                          ? "text-blue-600"
                          : isCompleted
                            ? "text-green-600"
                            : isAccessible
                              ? "text-gray-900"
                              : "text-gray-400"
                          }`}
                      >
                        {step.title}
                      </p>
                      <p
                        className={`text-xs truncate ${isActive
                          ? "text-blue-500"
                          : isCompleted
                            ? "text-green-500"
                            : isAccessible
                              ? "text-gray-500"
                              : "text-gray-400"
                          }`}
                      >
                        {step.description}
                      </p>
                    </div>

                    {/* Step title for mobile */}
                    <div className="ml-1 sm:hidden">
                      <p
                        className={`text-xs font-medium ${isActive
                          ? "text-blue-600"
                          : isCompleted
                            ? "text-green-600"
                            : isAccessible
                              ? "text-gray-900"
                              : "text-gray-400"
                          }`}
                      >
                        {step.title}
                      </p>
                    </div>
                  </div>

                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 sm:mx-4 min-w-4 sm:min-w-8 ${stepValidation[step.number as 1 | 2 | 3 | 4] ? "bg-green-300" : "bg-gray-200"
                        }`}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Step 1: Site Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Basic Site Information */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuInfo className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600" />
                  Basic Site Information
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site ID *</label>
                    <input
                      type="text"
                      value={formData.site_id}
                      onChange={(e) => updateFormData("site_id", e.target.value)}
                      placeholder="Enter unique site identifier (e.g., SITE001)"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.site_id ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={loading || submitting}
                    />
                    {errors.site_id && <p className="mt-1 text-xs text-red-600">{errors.site_id}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Activity</label>
                      <input
                        type="text"
                        value={formData.activity || ""}
                        onChange={(e) => updateFormData("activity", e.target.value)}
                        placeholder="Enter activity name"
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={loading || submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Reference Number</label>
                      <input
                        type="text"
                        value={formData.reference_number || ""}
                        onChange={(e) => updateFormData("reference_number", e.target.value)}
                        placeholder="Enter reference number"
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={loading || submitting}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Client Allocation Date</label>
                    <input
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      value={formData.client_allocation_date || ""}
                      onChange={(e) => updateFormData("client_allocation_date", e.target.value)}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.client_allocation_date ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={loading || submitting}
                    />
                    {errors.client_allocation_date && <p className="mt-1 text-xs text-red-600">{errors.client_allocation_date}</p>}
                  </div>
                </div>
              </div>

              {/* Project Assignment */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuNetwork className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600" />
                  Project Assignment
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Project *</label>
                  <SelectWithSearch
                    value={activeProjects.find(p => p.id === formData.project_id) ? { id: formData.project_id, label: activeProjects.find(p => p.id === formData.project_id)!.project_name, value: formData.project_id } : null}
                    onChange={(option) => updateFormData("project_id", option ? option.id : 0)}
                    options={activeProjects.map(p => ({ id: p.id, label: p.project_name, value: p.id }))}
                    placeholder="Select a project"
                    disabled={loading || submitting}
                  />
                  {errors.project_id && <p className="mt-1 text-xs text-red-600">{errors.project_id}</p>}

                  {activeProjects.length === 0 && (
                    <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
                      <LuInfo className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium">No active projects available</p>
                        <p className="text-xs mt-1">Please ensure there are active projects before creating a site.</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client Project Name</label>
                  <input
                    type="text"
                    value={formData.client_project_name || ""}
                    onChange={(e) => updateFormData("client_project_name", e.target.value)}
                    placeholder="Enter client project name"
                    className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    disabled={loading || submitting}
                  />
                </div> */}
              </div>

              {/* Selected Project Info */}
              {selectedProject && (
                <div className="bg-blue-50 border border-blue-200 rounded p-4 sm:p-6">
                  <h4 className="text-base sm:text-lg font-semibold text-blue-800 mb-4">Selected Project Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">Project Name</label>
                      <p className="text-sm text-blue-900">{selectedProject.project_name}</p>
                    </div>
                    {selectedProject.client && (
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-1">Client</label>
                        <p className="text-sm text-blue-900">{selectedProject.client.client_name}</p>
                      </div>
                    )}
                    {selectedProject.activity && (
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-1">Activity</label>
                        <p className="text-sm text-blue-900">{selectedProject.activity.activity_name}</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">Status</label>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {selectedProject.status}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Location Details */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuMapPin className="w-4 sm:w-5 h-4 sm:h-5 text-green-600" />
                  Site Location
                </h3>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
                      <input
                        type="text"
                        value={formData.site_name || ""}
                        onChange={(e) => updateFormData("site_name", e.target.value)}
                        placeholder="Enter site name"
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={loading || submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Site Priority</label>
                      <select
                        value={formData.site_priority || "medium"}
                        onChange={(e) => updateFormData("site_priority", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={loading || submitting}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Address</label>
                    <textarea
                      value={formData.site_address || ""}
                      onChange={(e) => updateFormData("site_address", e.target.value)}
                      placeholder="Enter complete site address"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                      disabled={loading || submitting}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                      <SelectWithSearch
                        value={selectedState}
                        onChange={handleStateChange}
                        placeholder="Select state"
                        disabled={loading || submitting}
                        apiEndpoint="/state-circle/list"
                        dataKey="states"
                        labelKey="state_name"
                        displayFormat={(item) => `${item.state_name} (${item.circle_name})`}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">District</label>
                      <SelectWithSearch
                        value={selectedDistrict}
                        onChange={handleDistrictChange}
                        placeholder="Select district"
                        disabled={loading || submitting || !selectedState}
                        apiEndpoint="/state-circle"
                        dataKey="districts"
                        dependencyId={selectedState?.id}
                        labelKey="name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                      <input
                        type="text"
                        value={formData.pincode || ""}
                        onChange={(e) => updateFormData("pincode", e.target.value)}
                        placeholder="Enter pincode"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.pincode ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                          }`}
                        disabled={loading || submitting}
                      />
                      {errors.pincode && <p className="mt-1 text-xs text-red-600">{errors.pincode}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* OpCo Details */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuBuilding className="w-4 sm:w-5 h-4 sm:h-5 text-orange-600" />
                  Operating Company Details
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">OpCo Name</label>
                    <input
                      type="text"
                      value={formData.opco_name || ""}
                      onChange={(e) => updateFormData("opco_name", e.target.value)}
                      placeholder="Enter operating company name"
                      className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={loading || submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">OpCo ID</label>
                    <input
                      type="text"
                      value={formData.opco_id || ""}
                      onChange={(e) => updateFormData("opco_id", e.target.value)}
                      placeholder="Enter operating company ID"
                      className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={loading || submitting}
                    />
                  </div>
                </div>
              </div>

              {/* Coordinates */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuGlobe className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600" />
                  GPS Coordinates
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      value={formData.site_latitude || ""}
                      onChange={(e) => updateFormData("site_latitude", e.target.value ? parseFloat(e.target.value) : undefined)}
                      placeholder="Enter latitude (e.g., 28.6139)"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.site_latitude ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={loading || submitting}
                    />
                    {errors.site_latitude && <p className="mt-1 text-xs text-red-600">{errors.site_latitude}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      value={formData.site_longitude || ""}
                      onChange={(e) => updateFormData("site_longitude", e.target.value ? parseFloat(e.target.value) : undefined)}
                      placeholder="Enter longitude (e.g., 77.2090)"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.site_longitude ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={loading || submitting}
                    />
                    {errors.site_longitude && <p className="mt-1 text-xs text-red-600">{errors.site_longitude}</p>}
                  </div>
                </div>

                <div className="mt-3 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded">
                  <LuInfo className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p>GPS coordinates help in precise site location identification and navigation.</p>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuClipboard className="w-4 sm:w-5 h-4 sm:h-5 text-indigo-600" />
                  Additional Site Information
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer Requirement</label>
                    <textarea
                      value={formData.customer_requirement || ""}
                      onChange={(e) => updateFormData("customer_requirement", e.target.value)}
                      placeholder="Enter customer requirements"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                      disabled={loading || submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Information 1</label>
                    <textarea
                      value={formData.site_information1 || ""}
                      onChange={(e) => updateFormData("site_information1", e.target.value)}
                      placeholder="Enter additional site information"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                      disabled={loading || submitting}
                    />
                  </div>

                  {visibleSiteInfoFields >= 2 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Site Information 2</label>
                      <textarea
                        value={formData.site_information2 || ""}
                        onChange={(e) => updateFormData("site_information2", e.target.value)}
                        placeholder="Enter additional site information"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                        disabled={loading || submitting}
                      />
                    </div>
                  )}

                  {visibleSiteInfoFields >= 3 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Site Information 3</label>
                      <textarea
                        value={formData.site_information3 || ""}
                        onChange={(e) => updateFormData("site_information3", e.target.value)}
                        placeholder="Enter additional site information"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                        disabled={loading || submitting}
                      />
                    </div>
                  )}

                  {visibleSiteInfoFields < 3 && (
                    <div className="flex justify-start">
                      <button
                        type="button"
                        onClick={() => setVisibleSiteInfoFields(prev => prev + 1)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading || submitting}
                      >
                        <LuPlus className="w-4 h-4" />
                        Add More
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Team & Contact */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* Team Members */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuUser className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600" />
                  Team Assignment
                </h3>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Technician Name</label>
                      <input
                        type="text"
                        value={formData.technician_name || ""}
                        onChange={(e) => updateFormData("technician_name", e.target.value)}
                        placeholder="Enter technician name"
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={loading || submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Technician Number</label>
                      <input
                        type="tel"
                        value={formData.technician_number || ""}
                        onChange={(e) => updateFormData("technician_number", e.target.value)}
                        placeholder="Enter technician contact number"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.technician_number ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                          }`}
                        disabled={loading || submitting}
                      />
                      {errors.technician_number && <p className="mt-1 text-xs text-red-600">{errors.technician_number}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">FSE Name</label>
                      <input
                        type="text"
                        value={formData.fse_name || ""}
                        onChange={(e) => updateFormData("fse_name", e.target.value)}
                        placeholder="Enter FSE name"
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={loading || submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">FSE Number</label>
                      <input
                        type="tel"
                        value={formData.fse_number || ""}
                        onChange={(e) => updateFormData("fse_number", e.target.value)}
                        placeholder="Enter FSE contact number"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.fse_number ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                          }`}
                        disabled={loading || submitting}
                      />
                      {errors.fse_number && <p className="mt-1 text-xs text-red-600">{errors.fse_number}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">AOM Name</label>
                      <input
                        type="text"
                        value={formData.aom_name || ""}
                        onChange={(e) => updateFormData("aom_name", e.target.value)}
                        placeholder="Enter AOM name"
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={loading || submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">AOM Number</label>
                      <input
                        type="tel"
                        value={formData.aom_number || ""}
                        onChange={(e) => updateFormData("aom_number", e.target.value)}
                        placeholder="Enter AOM contact number"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus-border-blue-500 transition-colors ${errors.aom_number ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                          }`}
                        disabled={loading || submitting}
                      />
                      {errors.aom_number && <p className="mt-1 text-xs text-red-600">{errors.aom_number}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuPhone className="w-4 sm:w-5 h-4 sm:h-5 text-green-600" />
                  Site Contact Information
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                    <input
                      type="text"
                      value={formData.contact_person || ""}
                      onChange={(e) => updateFormData("contact_person", e.target.value)}
                      placeholder="Enter contact person name"
                      className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={loading || submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                    <input
                      type="tel"
                      value={formData.contact_number || ""}
                      onChange={(e) => updateFormData("contact_number", e.target.value)}
                      placeholder="Enter contact number"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.contact_number ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={loading || submitting}
                    />
                    {errors.contact_number && <p className="mt-1 text-xs text-red-600">{errors.contact_number}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: User Assignments */}
          {currentStep === 4 && (
            <div className="space-y-6">
              {/* User Assignments */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuUser className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600" />
                  User Role Assignments
                </h3>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Site Engineer</label>
                      <SelectWithSearch
                        value={formData.site_engineer_userid && users.find(u => u.id === formData.site_engineer_userid) ? { id: formData.site_engineer_userid, label: users.find(u => u.id === formData.site_engineer_userid)!.first_name, value: formData.site_engineer_userid } : null}
                        onChange={(option) => updateFormData("site_engineer_userid", option ? option.id : undefined)}
                        options={users.map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name} (${u.role.name})`, value: u.id }))}
                        placeholder="Select site engineer"
                        disabled={loading || submitting || usersLoading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Technical Associate</label>
                      <SelectWithSearch
                        value={formData.ta_userid && users.find(u => u.id === formData.ta_userid) ? { id: formData.ta_userid, label: users.find(u => u.id === formData.ta_userid)!.first_name, value: formData.ta_userid } : null}
                        onChange={(option) => updateFormData("ta_userid", option ? option.id : undefined)}
                        options={users.map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name} (${u.role.name})`, value: u.id }))}
                        placeholder="Select Technical Associate"
                        disabled={loading || submitting || usersLoading}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">MIS</label>
                      <SelectWithSearch
                        value={formData.mis_userid && users.find(u => u.id === formData.mis_userid) ? { id: formData.mis_userid, label: users.find(u => u.id === formData.mis_userid)!.first_name, value: formData.mis_userid } : null}
                        onChange={(option) => updateFormData("mis_userid", option ? option.id : undefined)}
                        options={users.map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name} (${u.role.name})`, value: u.id }))}
                        placeholder="Select MIS user"
                        disabled={loading || submitting || usersLoading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Report Designer</label>
                      <SelectWithSearch
                        value={formData.report_designer_userid && users.find(u => u.id === formData.report_designer_userid) ? { id: formData.report_designer_userid, label: users.find(u => u.id === formData.report_designer_userid)!.first_name, value: formData.report_designer_userid } : null}
                        onChange={(option) => updateFormData("report_designer_userid", option ? option.id : undefined)}
                        options={users.map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name} (${u.role.name})`, value: u.id }))}
                        placeholder="Select report designer"
                        disabled={loading || submitting || usersLoading}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Validator</label>
                    <SelectWithSearch
                      value={formData.validator_userid && users.find(u => u.id === formData.validator_userid) ? { id: formData.validator_userid, label: users.find(u => u.id === formData.validator_userid)!.first_name, value: formData.validator_userid } : null}
                      onChange={(option) => updateFormData("validator_userid", option ? option.id : undefined)}
                      options={users.map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name} (${u.role.name})`, value: u.id }))}
                      placeholder="Select validator"
                      disabled={loading || submitting || usersLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Purchase Order Details */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {/* Purchase Request */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuFileText className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600" />
                  Purchase Request Details
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PR Number</label>
                    <input
                      type="text"
                      value={formData.pr_number || ""}
                      onChange={(e) => updateFormData("pr_number", e.target.value)}
                      placeholder="Enter PR number"
                      className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={loading || submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PR Date</label>
                    <input
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      value={formData.pr_date || ""}
                      onChange={(e) => updateFormData("pr_date", e.target.value)}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.pr_date ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={loading || submitting}
                    />
                    {errors.pr_date && <p className="mt-1 text-xs text-red-600">{errors.pr_date}</p>}
                  </div>
                </div>
              </div>

              {/* Purchase Order */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuClipboard className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600" />
                  Purchase Order Details
                </h3>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">PO Number</label>
                      <input
                        type="text"
                        value={formData.po_number || ""}
                        onChange={(e) => updateFormData("po_number", e.target.value)}
                        placeholder="Enter PO number"
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={loading || submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">PO Date</label>
                      <input
                        type="date"
                        max={new Date().toISOString().split("T")[0]}
                        value={formData.po_date || ""}
                        onChange={(e) => updateFormData("po_date", e.target.value)}
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.po_date ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                          }`}
                        disabled={loading || submitting}
                      />
                      {errors.po_date && <p className="mt-1 text-xs text-red-600">{errors.po_date}</p>}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="is_po_no_shared_at_allocation"
                      checked={formData.is_po_no_shared_at_allocation || false}
                      onChange={(e) => updateFormData("is_po_no_shared_at_allocation", e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded  focus:ring-blue-500"
                      disabled={loading || submitting}
                    />
                    <label htmlFor="is_po_no_shared_at_allocation" className="text-sm font-medium text-gray-700">
                      PO number shared at allocation
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}


        </div>

        {/* Footer - Fixed */}
        <div className="border-t border-gray-200 bg-white p-4 sm:p-6 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Progress indicator */}
            <div className="flex items-center gap-2 text-sm text-gray-600 order-2 sm:order-1">
              <span>Step {currentStep} of {steps.length}</span>
              <div className="w-24 bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / steps.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3 order-1 sm:order-2 w-full sm:w-auto">
              {currentStep > 1 && (
                <button
                  onClick={handlePrevious}
                  disabled={loading || submitting}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 sm:flex-none justify-center"
                >
                  Previous
                </button>
              )}

              {currentStep < steps.length ? (
                <button
                  onClick={handleNext}
                  disabled={loading || submitting || !stepValidation[currentStep as 1 | 2 | 3 | 4]}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 sm:flex-none justify-center"
                >
                  Next
                  <LuChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading || submitting || !Object.values(stepValidation).slice(0, 4).every(Boolean)}
                  className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 sm:flex-none justify-center"
                >
                  {submitting ? (
                    <>
                      <LuLoader className="w-4 h-4 animate-spin" />
                      Creating Site...
                    </>
                  ) : (
                    <>
                      <LuPlus className="w-4 h-4" />
                      Create Site
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={() => {
          setShowCloseModal(false);
          onClose();
        }}
      />

    </>
  );
};
